// Inline widgets rendered inside chat bubbles when Infi asks for input.
import { useState, useRef, useEffect } from "react";
import { Upload, Camera, Calendar, CheckCircle2, AlertTriangle, Shield } from "lucide-react";

import * as api from "../api";
import { maybeDownscaleImage } from "../lib/imageDownscale";

export function ConsentWidget({ caseId, onConsented, disabled }) {
  const [notice, setNotice] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getConsentNotice().then(setNotice).catch((e) =>
      setError(e?.response?.data?.detail || "Could not load consent notice"));
  }, []);

  async function submit() {
    if (!agreed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.recordConsent(caseId);
      onConsented?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record consent");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border border-slate-300 rounded-xl bg-white overflow-hidden"
         data-testid="consent-widget">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <Shield size={16} className="text-[#C5283F]" />
        <span className="font-medium text-sm text-slate-900">
          Before we collect your documents — a quick consent
        </span>
      </div>
      <div className="px-4 py-3 max-h-72 overflow-y-auto text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed"
           data-testid="consent-text">
        {notice ? notice.text : "Loading…"}
      </div>
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={disabled || busy || !notice}
            data-testid="consent-agree-checkbox"
            className="mt-0.5 h-4 w-4 accent-[#C5283F]"
          />
          <span className="text-sm text-slate-800">
            I agree to Infin/Mavenstry collecting and processing my data as
            described above for the purpose of incorporating my Private Limited
            Company.
          </span>
        </label>
        {notice && (
          <div className="mt-2 text-[11px] text-slate-500 font-mono">
            v: {notice.version}
          </div>
        )}
        {error && (
          <div className="mt-2 text-xs text-red-700" data-testid="consent-error">{error}</div>
        )}
        <button
          onClick={submit}
          disabled={!agreed || busy || disabled || !notice}
          data-testid="consent-submit-btn"
          className="mt-3 w-full bg-[#C5283F] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#A82035] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Recording…" : "I agree — continue"}
        </button>
      </div>
    </div>
  );
}

export function RadioWidget({ options, onSelect, disabled }) {
  return (
    <div className="flex flex-col gap-2 mt-3" data-testid="radio-widget">
      {options.map((opt, i) => (
        <button
          key={typeof opt === "string" ? opt : `opt-${i}`}
          onClick={() => onSelect(opt)}
          disabled={disabled}
          data-testid={`radio-option-${i}`}
          className="text-left px-4 py-3 border border-slate-300 rounded-lg bg-white hover:border-[#C5283F] hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function FileUploadWidget({ accept = "image/*,application/pdf", target, onUpload, disabled, busy }) {
  const ref = useRef(null);
  const cameraRef = useRef(null);
  const [drag, setDrag] = useState(false);

  async function handleFile(f) {
    if (!f) return;
    // WO 8.2 — client-side downscale BEFORE handing to the uploader.
    let prepared = f;
    try {
      prepared = await maybeDownscaleImage(f);
    } catch (e) {
      // Client-side downscale is a best-effort optimisation; if the
      // browser fails (e.g. canvas blocked, OOM on huge HEIC), fall
      // back to the original file. Log so a regression in
      // imageDownscale.js is at least visible in devtools.
      console.warn("[FileUploadWidget] image downscale failed; uploading original:", e);
    }
    onUpload(prepared, target);
  }

  return (
    <div className="mt-3" data-testid="file-upload-widget">
      <div
        className={[
          "border-2 border-dashed rounded-xl p-6 text-center transition-colors",
          drag ? "border-[#C5283F] bg-slate-50" : "border-slate-300 bg-white",
          disabled ? "opacity-50" : "cursor-pointer hover:bg-slate-50 hover:border-[#C5283F]",
        ].join(" ")}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          if (disabled) return;
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !disabled && ref.current?.click()}
      >
        <Upload size={20} className="mx-auto mb-2 text-slate-500" />
        <div className="text-sm font-medium text-slate-700">
          {busy ? "Uploading…" : "Click or drop a file here"}
        </div>
        <div className="text-xs text-slate-500 mt-1">JPG, PNG, or PDF · up to 10 MB</div>
        <input
          ref={ref}
          type="file"
          accept={accept}
          className="hidden"
          data-testid="file-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={disabled}
        />
      </div>
      <button
        onClick={() => cameraRef.current?.click()}
        disabled={disabled}
        data-testid="camera-capture-btn"
        className="mt-2 w-full text-sm text-slate-600 hover:text-slate-900 inline-flex items-center justify-center gap-1.5 py-2 rounded-md border border-slate-200 bg-white sm:hidden disabled:opacity-50"
      >
        <Camera size={14} /> Take a photo instead
      </button>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

export function DatePickerWidget({ onPick, disabled }) {
  const [val, setVal] = useState("");
  return (
    <div className="mt-3 flex gap-2 items-center" data-testid="date-picker-widget">
      <div className="relative flex-1">
        <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="date"
          value={val}
          disabled={disabled}
          onChange={(e) => setVal(e.target.value)}
          data-testid="date-input"
          className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C5283F] focus:border-transparent text-sm"
        />
      </div>
      <button
        onClick={() => val && onPick(val)}
        disabled={disabled || !val}
        data-testid="date-confirm-btn"
        className="bg-[#C5283F] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#A82035] disabled:opacity-50"
      >
        Use
      </button>
    </div>
  );
}

function ConfirmField({ k, label, type = "text", placeholder, form, update, isLow }) {
  const low = isLow(k);
  return (
    <div className="mb-3">
      <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold block mb-1">
        {label}
      </label>
      <input
        type={type}
        value={form[k]}
        placeholder={placeholder}
        onChange={(e) => update(k, e.target.value)}
        data-testid={`confirm-field-${k}`}
        className={[
          "w-full text-sm rounded-md p-2 outline-none transition-colors",
          low
            ? "low-conf border"
            : "bg-slate-50 border border-transparent focus:bg-white focus:border-[#C5283F]",
        ].join(" ")}
      />
      {low && (
        <div className="text-[11px] text-amber-800 mt-1 flex items-center gap-1 font-medium">
          <AlertTriangle size={11} /> please check this one
        </div>
      )}
    </div>
  );
}

export function StakeholderConfirmationCard({ stakeholder, onConfirm, onCancel, disabled }) {
  const lowConfList = stakeholder?.data_provenance?.low_confidence_fields || [];
  const [form, setForm] = useState(() => ({
    role: stakeholder.role || "DIRECTOR_AND_SHAREHOLDER",
    share_percent: stakeholder.share_percent ?? "",
    first_name: stakeholder.personal?.first_name || "",
    middle_name: stakeholder.personal?.middle_name || "",
    last_name: stakeholder.personal?.last_name || "",
    fathers_name: stakeholder.personal?.fathers_name || "",
    gender: stakeholder.personal?.gender || "",
    date_of_birth: stakeholder.personal?.date_of_birth || "",
    place_of_birth: stakeholder.personal?.place_of_birth || "",
    pan: stakeholder.identity?.pan || "",
    aadhaar_last4: stakeholder.identity?.aadhaar_last4 || "",
    email: stakeholder.contact?.email || "",
    mobile: stakeholder.contact?.mobile || "",
  }));

  const isLow = (key) => lowConfList.some((k) => k.endsWith(`.${key}`));
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fieldProps = { form, update, isLow };

  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="stakeholder-confirmation-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Review stakeholder · {stakeholder.stakeholder_id}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ConfirmField k="first_name" label="First name" {...fieldProps} />
        <ConfirmField k="last_name" label="Last name" {...fieldProps} />
        <ConfirmField k="middle_name" label="Middle name" {...fieldProps} />
        <ConfirmField k="fathers_name" label="Father's name" {...fieldProps} />
        <ConfirmField k="date_of_birth" label="Date of birth" placeholder="YYYY-MM-DD" {...fieldProps} />
        <ConfirmField k="place_of_birth" label="Place of birth" {...fieldProps} />
        <ConfirmField k="pan" label="PAN" {...fieldProps} />
        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold block mb-1">
            Aadhaar (masked)
          </label>
          <div className="font-mono text-sm tracking-widest text-slate-700 bg-slate-50 border border-transparent p-2 rounded-md">
            •••• •••• {form.aadhaar_last4 || "----"}
          </div>
        </div>
        <ConfirmField k="email" label="Email" {...fieldProps} />
        <ConfirmField k="mobile" label="Mobile" {...fieldProps} />
        <ConfirmField k="share_percent" label="Share %" type="number" {...fieldProps} />
        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold block mb-1">
            Role
          </label>
          <select
            value={form.role}
            onChange={(e) => update("role", e.target.value)}
            data-testid="confirm-field-role"
            className="w-full bg-slate-50 border border-transparent text-sm p-2 rounded-md focus:bg-white focus:border-[#C5283F] outline-none"
          >
            <option value="DIRECTOR">Director</option>
            <option value="SHAREHOLDER">Shareholder</option>
            <option value="DIRECTOR_AND_SHAREHOLDER">Director & Shareholder</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button
          onClick={() => onConfirm(form)}
          disabled={disabled}
          data-testid="confirm-stakeholder-btn"
          className="flex-1 bg-[#C5283F] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#A82035] disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          <CheckCircle2 size={14} /> Confirm
        </button>
        <button
          onClick={onCancel}
          disabled={disabled}
          data-testid="cancel-confirm-btn"
          className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          Edit later
        </button>
      </div>
    </div>
  );
}
